import styled from 'styled-components';
import React from 'react';
import { TextField, Autocomplete } from '@mui/material';
import ChevronDown from 'design-system/src/assets/icons/chevronDown.svg?react';
import { focusVisibleStylesNoWrapper, standardTransitionStyles } from 'design-system/src/sharedStyles';

// TODO: style text
// TODO: style overlay
// TODO: instead of using classes here, pass custom components into the slotProps prop
//       https://mui.com/material-ui/api/autocomplete/?srsltid=AfmBOor-HHml6QfhYN_I7BA0nOiXYMW0ZsPkbUZZnZyR022q80ttFepP#autocomplete-prop-slotProps
const TypeaheadContainer = styled.div<{ $hasError?: boolean }>`
  * {
    // to avoid the focus outline being cut off
    overflow: visible;
  }

  > .MuiAutocomplete-root {
    // if the height isn't set, the height will shift by 1px when an item is selected
    height: 44px;

    > .MuiFormControl-root {
      height: 44px;

      > .MuiInputBase-root {
        padding: 0;
        height: 44px;

        > [role='combobox'] {
          padding: 10px 12px;

          // TODO(typography): use the typography styles after they're exported
          font-family: 'DM Sans', system-ui, Helvetica, Arial, sans-serif;
          font-size: 0.9375rem;
          font-weight: 400;
          line-height: 133.333%;
        }

        > fieldset {
          border-radius: var(--border-radius-medium);
          border: 1px solid var(--grayscale-100) !important;
          ${standardTransitionStyles}

          ${({ $hasError }) => $hasError && 'border: 1px solid var(--red-400);'}
        }

        // stops the empty legend from breaking the focus outline of the input field
        > fieldset > legend {
          width: 0px;
        }

        button {
          color: var(--grayscale-900);
          right: 2px;
          transition: transform 0.2s ease-out;
          top: auto;
        }

        &.Mui-focused > fieldset {
          border: 1px solid var(--grayscale-100) !important;
          ${focusVisibleStylesNoWrapper}
        }

        &.Mui-disabled {
          background-color: var(--grayscale-50);

          // targeting everthing because there are several elements with cursor: default
          > * {
            cursor: not-allowed !important;
          }

          > svg {
            color: var(--grayscale-100);
          }
        }
      }
    }
  }
`;

export type TypeaheadOption = {
  description: string;
  value: string;
};

type TypeaheadProps = {
  options: TypeaheadOption[];
  label?: string;
  // used to connect an external label to the input
  labelId?: string;
  onChange: (value: string) => void;
  value?: string;
  // Allows the consumer to pass in a styled textfield
  textfieldComponent?: React.ElementType;
  // style the input field to make it visually appear to have an error, but does not show an error message
  hasError?: boolean;
  // Placeholder text shown when no value is selected
  placeholder?: string;
  // Disable the typeahead input
  disabled?: boolean;
};

const Typeahead: React.FC<TypeaheadProps> = ({
  options,
  label,
  labelId,
  onChange,
  value,
  textfieldComponent,
  hasError,
  placeholder,
  disabled,
  // Allow passing through additional props to the container, e.g. analytics props
  ...rest
}) => {
  if (!label && !labelId) {
    throw new Error('label or labelId is required for accessibility purposes');
  }

  const getOptionForValue = (value: string) => {
    return options.find((option) => {
      return option.value === value;
    });
  };

  const TextfieldToUse = textfieldComponent || TextField;

  return (
    <TypeaheadContainer $hasError={hasError} {...rest}>
      <Autocomplete
        options={options}
        getOptionLabel={(option: TypeaheadOption) => option.description}
        value={value ? getOptionForValue(value) : undefined}
        onChange={(_, newValue) => {
          onChange(newValue ? newValue.value : '');
        }}
        disabled={disabled}
        renderInput={(params) => (
          <TextfieldToUse
            {...params}
            label={label}
            aria-labelledby={labelId}
            variant="outlined"
            fullWidth
            placeholder={placeholder}
          />
        )}
        popupIcon={<ChevronDown />}
      />
    </TypeaheadContainer>
  );
};

export default Typeahead;
